// Herbst-Aktion UI-Komponenten
import React from "react";
import { Link } from "react-router-dom";
import { splitDHMS, fmtEur, trackPromo, usePromotion, promoProbablyActive } from "../../lib/promotion";
import { Sparkles, Clock, ArrowRight, X } from "lucide-react";

/* ====================== BADGE ====================== */
export function PromoBadge({ text, size = "md", className = "" }) {
  const sizeCls = size === "lg" ? "text-base py-2 px-4" : "text-xs py-1 px-3";
  return (
    <span
      data-testid="promo-badge"
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold text-white
                  bg-gradient-to-r from-[#B4420F] via-[#D8691C] to-[#E8A83D]
                  shadow-md shadow-amber-900/20 relative overflow-hidden promo-shine ${sizeCls} ${className}`}
    >
      <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
      <span className="relative z-10">{text}</span>
    </span>
  );
}

/* ====================== COUNTDOWN ====================== */
function CDCell({ v, label }) {
  const padded = String(v).padStart(2, "0");
  return (
    <div className="flex flex-col items-center">
      <div className="font-heading text-2xl sm:text-3xl text-ink tabular-nums bg-white/80 backdrop-blur px-2 py-1 min-w-[2.5rem] text-center rounded-md shadow-sm border border-amber-200/60">
        {padded}
      </div>
      <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-ink-muted mt-1">{label}</div>
    </div>
  );
}

export function CountdownClock({ secondsLeft, compact = false, onClick, slug }) {
  const { d, h, m, s } = splitDHMS(secondsLeft);
  if (compact) {
    return (
      <span
        className="inline-flex items-center gap-1 font-semibold tabular-nums text-ink"
        data-testid="promo-countdown-compact"
        onClick={() => { onClick?.(); trackPromo("countdown", slug, "compact"); }}
      >
        <Clock className="w-3.5 h-3.5" />
        {d > 0 && <>{d}<span className="opacity-60">T</span></>}
        {String(h).padStart(2, "0")}<span className="opacity-60">Std</span>
      </span>
    );
  }
  return (
    <div
      className="inline-flex items-center gap-2 sm:gap-3"
      data-testid="promo-countdown"
      onClick={() => { onClick?.(); trackPromo("countdown", slug, "full"); }}
    >
      <CDCell v={d} label="Tage" />
      <span className="font-heading text-xl text-amber-700/60">:</span>
      <CDCell v={h} label="Std" />
      <span className="font-heading text-xl text-amber-700/60">:</span>
      <CDCell v={m} label="Min" />
      <span className="font-heading text-xl text-amber-700/60">:</span>
      <CDCell v={s} label="Sek" />
    </div>
  );
}

/* ====================== PRODUCT PROMO BLOCK ====================== */
/**
 * Für die Produkt-Detailseite oberhalb des Kaufen-Buttons des Komplettpakets.
 * Erwartet: slug (string). Blendet sich aus, wenn Promo inaktiv oder slug nicht Teil der Aktion.
 */
export function ProductPromoBlock({ slug }) {
  const { promo, secondsLeft, active } = usePromotion();
  if (!active || !promo?.products?.[slug]) return null;
  const p = promo.products[slug];
  return (
    <div
      data-testid="product-promo-block"
      className="rounded-xl border border-amber-200/70 bg-gradient-to-br from-amber-50/70 to-rose-50/50 p-4 sm:p-5 mb-4"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <PromoBadge text={promo.badge_text} />
        <div className="text-xs text-ink-muted">Aktionspreis gilt nur bis 30.11.2026 · 23:59 Uhr</div>
      </div>
      <div className="mt-3">
        <CountdownClock secondsLeft={secondsLeft} slug={slug} />
      </div>
      <div className="mt-3 text-sm text-ink-muted">
        {promo.future_note_prefix}
        {" "}
        <b>Magic Mirror {fmtEur(promo.products["magic-mirror"]?.normal_gross)} · Topbase {fmtEur(promo.products["fotobox-topbase"]?.normal_gross)} · Premium {fmtEur(promo.products["fotobox-premium"]?.normal_gross)}</b>
      </div>
    </div>
  );
}

/* ====================== HERO BANNER ====================== */
export function PromoHeroBanner({ className = "" }) {
  const { promo, secondsLeft, active, loaded } = usePromotion();
  // Solange die Aktion noch geladen wird, Platz freihalten (sonst rutscht die Seite nach unten, Google: CLS)
  if (!loaded) return promoProbablyActive() && <div className={`min-h-[355px] md:min-h-[324px] lg:min-h-[188px] ${className}`} aria-hidden="true" />;
  if (!active) return null;
  return (
    <div
      data-testid="promo-hero-banner"
      className={`promo-hero-enter relative overflow-hidden rounded-2xl border border-amber-200/60
                  bg-gradient-to-r from-rose-50 via-amber-50 to-amber-100 shadow-sm ${className}`}
    >
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-amber-400/10 rounded-full blur-3xl" aria-hidden />
      <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-rose-400/10 rounded-full blur-3xl" aria-hidden />
      <div className="relative px-5 sm:px-8 py-6 sm:py-7 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
        <div>
          <PromoBadge text={promo.banner_title} size="lg" />
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-ink mt-3 leading-tight">
            {promo.banner_subtitle}
          </h2>
          <div className="text-sm text-ink-muted mt-2">
            {promo.future_note_prefix} Magic Mirror {fmtEur(promo.products["magic-mirror"]?.normal_gross)} · Topbase {fmtEur(promo.products["fotobox-topbase"]?.normal_gross)} · Premium {fmtEur(promo.products["fotobox-premium"]?.normal_gross)}
          </div>
        </div>
        <div className="flex flex-col items-start lg:items-end gap-3 shrink-0">
          <CountdownClock secondsLeft={secondsLeft} />
          <Link
            to="/produkte"
            className="btn-accent"
            data-testid="promo-hero-cta"
            onClick={() => trackPromo("banner", null, "hero")}
          >
            {promo.banner_cta} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ====================== MOBILE STICKY BAR ====================== */
export function PromoStickyMobile() {
  const { promo, secondsLeft, active } = usePromotion();
  const [dismissed, setDismissed] = React.useState(() => {
    try { return sessionStorage.getItem("promo_sticky_dismissed") === "1"; } catch { return false; }
  });
  React.useEffect(() => {
    const on = !!(active && !dismissed);
    document.body.classList.toggle("has-promo-sticky", on);
    return () => { document.body.classList.remove("has-promo-sticky"); };
  }, [active, dismissed]);
  // Englischer Bereich (/en): Leiste komplett auf Englisch
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const en = path === "/en" || path.startsWith("/en/");
  const nl = path === "/nl" || path.startsWith("/nl/"); // Niederländischer Bereich (/nl)
  if (!active || dismissed) return null;
  const { d, h } = splitDHMS(secondsLeft);
  const label = nl
    ? (d > 0 ? `${d} d ${String(h).padStart(2, "0")} u` : `${String(h).padStart(2, "0")} u`)
    : en
    ? (d > 0 ? `${d} d ${String(h).padStart(2, "0")} h` : `${String(h).padStart(2, "0")} h`)
    : (d > 0 ? `${d} T ${String(h).padStart(2, "0")} Std` : `${String(h).padStart(2, "0")} Std`);

  const scrollToKomplett = (e) => {
    trackPromo("sticky", null, "mobile_bar");
    // Falls bereits auf Produktseite: zum Komplettpaket scrollen
    const el = document.querySelector('[data-testid="product-promo-block"]');
    if (el) {
      e.preventDefault();
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div
      data-testid="promo-sticky-mobile"
      className="fixed bottom-0 inset-x-0 z-40 lg:hidden"
    >
      <div className="mx-2 mb-2 rounded-xl shadow-lg border border-amber-200/70 bg-white/95 backdrop-blur px-3 py-2 flex items-center gap-2">
        <PromoBadge text={nl ? "Herfst" : en ? "Autumn" : "Herbst"} />
        <div className="flex-1 min-w-0 text-xs text-ink leading-tight">
          <div className="font-semibold truncate">{nl ? "Herfstprijs eindigt over" : en ? "Autumn price ends in" : promo.sticky_prefix} {label}</div>
          <div className="text-ink-muted truncate">{nl ? "Complete pakketten voor de herfstprijs" : en ? "Complete packages at autumn price" : "Komplettpakete im Herbstpreis"}</div>
        </div>
        <Link
          to={nl ? "/nl/fotoboxen" : en ? "/en/photo-booths" : "/produkte"}
          onClick={scrollToKomplett}
          className="rounded-full bg-amber-500 hover:bg-amber-400 text-ink text-xs font-semibold px-3 py-1.5 whitespace-nowrap"
        >
          {nl ? "Nu voordeel" : en ? "Save now" : promo.sticky_cta}
        </Link>
        <button
          type="button"
          aria-label={nl ? "Sluiten" : en ? "Close" : "Schließen"}
          onClick={() => { setDismissed(true); try { sessionStorage.setItem("promo_sticky_dismissed", "1"); } catch {} }}
          className="p-1 text-ink-muted hover:text-ink"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
